import React, { useEffect } from "react";
import { Container, Button } from "react-bootstrap";
import { useLocation, useNavigate, Link } from "react-router-dom";
import Row from 'react-bootstrap/Row';
import DonateBanner from "./DonateBanner";
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

const LOGO_SRC = `${process.env.PUBLIC_URL}/main_logo.png`;

const AUTH_PATHS = new Set([
  '/',
  '/register',
  '/forgot-username',
  '/forgot-password',
  '/reset-password',
]);

function getLogoTarget(pathname) {
  return AUTH_PATHS.has(pathname) ? '/' : '/main';
}

function AppLogo({ onClick, className = '' }) {
  return (
    <button
      type="button"
      className={`app-logo-btn ${className}`.trim()}
      onClick={onClick}
      aria-label="teachers-app.com"
    >
      <img src={LOGO_SRC} alt="teachers-app.com" className="app-logo-img" />
    </button>
  );
}

// Footer Component
const Footer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const version = process.env.REACT_APP_LAST_UPDATE;

  return (
    <footer className="bg-success footer-custom py-2 py-md-2">
      <div className="last-update-block small text-end pe-2 pb-1">
        Τελευταία ενημέρωση: {version}
      </div>
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-center text-center text-md-start">
        
      {/* Left: Copyright */}
      <div className="mb-1 mb-md-0 small ps-2">
        &copy; {new Date().getFullYear()} teachers-app.com. All Rights Reserved.
      </div>

    {/* Right: Footer links */}
    <div className="mt-1 mt-md-0 small pe-2">
      {location.pathname === "/terms-of-use" || location.pathname === "/privacy-policy" ? (
        <></>
      ) : location.pathname !== "/" &&
        location.pathname !== "/register" &&
        location.pathname !== "/forgot-username" &&
        location.pathname !== "/forgot-password" &&
        location.pathname !== "/reset-password" ? (
        <>
          <Link to="/terms-of-use" target="_blank" className="text-decoration-none footer-links pe-1">
            Όροι χρήσης
          </Link> |{" "}
          <Link to="/privacy-policy" target="_blank" className="text-decoration-none footer-links ps-1 pe-1">
            Προστασία Δεδομένων (GDPR)
          </Link> |{" "}
          <Link to="/contact-support" className="text-decoration-none footer-links ps-1">
            Επικοινωνία
          </Link>
        </>
      ) : (
        <>
          <Link to="/terms-of-use" target="_blank" className="text-decoration-none footer-links pe-1">
            Όροι χρήσης
          </Link> |{" "}
          <Link to="/privacy-policy" target="_blank" className="text-decoration-none footer-links ps-1">
            Προστασία Δεδομένων (GDPR)
          </Link>
        </>
      )}
    </div>

  </div>
    </footer>
  );
};
  
// Header Component
const Header = ({ handleLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  

  const pageTitles = {
    "/": "Είσοδος",
    "/main": "Χρήσιμα εργαλεία για αναπληρωτές",
    "/ranking-per-state": "Αριθμός προσλήψεων ανά ημερομηνία πρόσληψης",
    "/ranking-per-score": "Έλεγχος περιοχων βάσει θέσης πίνακα",
    "/ranking-personal-details": "Εύρεση θέσης πίνακα",
    "/appointment-date": "Ημερομηνία πρόσληψης βάσει σειράς πίνακα",
    "/ranking-above-me": "Συνολικός αριθμός ανθρώπων που προηγούνται",
    "/rent-prices": "Τιμές ενοικίου",
    "/register": "Εγγραφή χρήστη",
    "/historical-data": "Ιστορικά δεδομένα προσλήψεων",
    "/transfers": "Μεταθέσεις εκπαιδευτικών",
    "/contact-support": "Φόρμα επικοινωνίας",
    "/forgot-username": "Υπενθύμιση ονόματος χρήστη",
    "/forgot-password": "Υπενθύμιση κωδικού πρόσβασης",
    "/reset-password": "Επαναφορά κωδικού πρόσβασης",
    "/historical-data-appointments": "Ιστορικά δεδομένα διορισμών",
    "/predict": "Εκτιμήσεις πιθανοτήτων πρόσληψης",
    "/donation-success": "Ευχαριστούμε",
    "/admin/user-premium": "Διαχείριση Premium"
  };

  const handleButtonClick = () => {
    if (location.pathname === '/register' || location.pathname === '/forgot-username'|| location.pathname === '/forgot-password' || location.pathname === '/reset-password') {
      navigate('/');
    } else {
      handleLogout();
    }
  };

  const handleMainPageClick = () => {
    navigate('/main');
  };

  const handleLogoClick = () => {
    navigate(getLogoTarget(location.pathname));
  };

  const currentTitle = pageTitles[location.pathname];
  const showBetaBadge = location.pathname === '/predict';

  const showHomeButton =
    location.pathname !== '/main' &&
    location.pathname !== '/' &&
    location.pathname !== '/register' &&
    location.pathname !== '/forgot-username' &&
    location.pathname !== '/forgot-password' &&
    location.pathname !== '/reset-password';

  const isAuthReturnPath = ['/register', '/forgot-username', '/forgot-password', '/reset-password'].includes(
    location.pathname
  );

  const showDonateBanner =
    location.pathname !== '/predict' &&
    location.pathname !== '/donation-success' &&
    location.pathname !== '/admin/user-premium' &&
    location.pathname !== '/' &&
    !isAuthReturnPath &&
    typeof window !== 'undefined' &&
    Boolean(localStorage.getItem('authToken'));

  const renderPageTitle = () => (
    <h6 className="header-page-title text-info-emphasis">
      {currentTitle}
      {showBetaBadge && (
        <span className="app-beta-badge header-beta-badge ms-1 ms-md-2" aria-label="Δοκιμαστική έκδοση">BETA</span>
      )}
    </h6>
  );

  return (
    <header >
  <div className="bg-success header-custom py-2 py-md-3">
    <Container className="header-container-inner">
      <div className="header-top-row">
        <div className="header-actions header-actions--start">
          {showHomeButton ? (
            <>
              <Button
                size="sm"
                variant="danger"
                className="d-md-none"
                onClick={handleMainPageClick}
                title="Αρχική"
              >
                <i className="bi bi-house" />
              </Button>
              <Button
                size="sm"
                variant="danger"
                className="d-none d-md-inline-block"
                onClick={handleMainPageClick}
              >
                Αρχική
              </Button>
            </>
          ) : (
            <span className="header-actions-spacer" aria-hidden="true" />
          )}
        </div>

        <div className="header-logo-center">
          <AppLogo onClick={handleLogoClick} />
        </div>

        <div className="header-actions header-actions--end">
          {location.pathname !== '/' ? (
            <>
              <Button
                size="sm"
                variant="danger"
                className="d-md-none"
                onClick={handleButtonClick}
                title={isAuthReturnPath ? 'Επιστροφή στην αρχική' : 'Αποσύνδεση'}
              >
                <i className={isAuthReturnPath ? 'bi bi-arrow-left-circle' : 'bi bi-power'} />
              </Button>
              <Button
                size="sm"
                variant="outline-danger"
                className="d-none d-md-inline-block"
                onClick={handleButtonClick}
              >
                {isAuthReturnPath ? 'Επιστροφή στην αρχική' : 'Αποσύνδεση'}
              </Button>
            </>
          ) : (
            <span className="header-actions-spacer" aria-hidden="true" />
          )}
        </div>
      </div>

      {currentTitle && (
        <div className="header-subtitle-row">
          {renderPageTitle()}
        </div>
      )}
    </Container>
  </div>
      {showDonateBanner && (
        <DonateBanner />
      )}

</header>

  );
};

// Export Components
export { Footer, Header };